import { pluralEn, type Count } from '../lib/format'

import type { Translations } from './types'

/** « alle 21:25 », but « all’01:25 »: the article before a clock time agrees with the hour. */
const atHour = (time: string): string => (/^0?1\D/.test(time) ? `all’${time}` : `alle ${time}`)

/**
 * Italian. Typed against the Russian source: every key and every function
 * signature must match. Amounts and dates arrive already formatted; the
 * dictionary supplies only the words around them.
 */
export const it: Translations = {
  common: {
    all: 'Max',
    back: 'Indietro',
    cancel: 'Annulla',
    confirm: 'Conferma',
    copy: 'Copia',
    copied: 'Copiato',
    open: 'Apri',
    retry: 'Riprova',
    save: 'Salva',
    close: 'Chiudi',
    loading: 'Caricamento',
    available: 'Disponibile',
    total: 'Totale',
    amount: 'Importo',
    network: 'Rete',
    asset: 'Valuta',
    status: 'Stato',
    date: 'Data',
    nothingYet: 'Ancora niente',
    somethingWentWrong: 'Qualcosa è andato storto',
    reload: 'Ricarica',
    crashHint: 'Impossibile mostrare la schermata. Ricarica l’app: fondi e richieste non sono interessati.',
  },

  nav: {
    label: 'Navigazione principale',
    home: 'Home',
    markets: 'Mercati',
    signals: 'Segnali',
    trade: 'Trading',
    wallet: 'Portafoglio',
  },

  auth: {
    signIn: 'Accesso',
    signInSubtitle: 'Accedi per tornare ai tuoi segnali e al tuo saldo',
    signInAction: 'Accedi',
    signUp: 'Registrazione',
    signUpSubtitle: 'Email e password: basta questo per iniziare',
    signUpSubtitleInvite: 'Email, password e codice invito: basta questo per iniziare',
    email: 'Email',
    emailPlaceholder: 'nome@esempio.com',
    password: 'Password',
    passwordHint: 'Almeno 8 caratteri, con lettere e numeri',
    passwordConfirm: 'Ripeti la password',
    referralCode: 'Codice invito',
    referralCodePlaceholder: 'Ad esempio, 8K3M7QPL',
    referralCodeHint: 'Facoltativo: se ti ha invitato un membro di 19CRT',
    referralCodeRequiredHint: 'Registrazione solo su invito: usa il link o il codice di chi ti ha invitato',
    /** The programme's live terms under a prefilled invitation code: what the newcomer gets and for what. */
    referralTerms: (deposit: string, asset: string, signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `${signals.text} ${pluralEn(signals, 'segnale bonus', 'segnali bonus')}` : null,
        promo === null ? null : `un bonus promozionale di ${promo} USDT`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0
        ? `Se il codice te l’ha dato un membro di 19CRT, l’invito vale dopo un primo deposito di almeno ${deposit} ${asset}`
        : `Se il codice te l’ha dato un membro di 19CRT, dopo un primo deposito di almeno ${deposit} ${asset} riceverai ${perks.join(' e ')}`
    },
    forgot: 'Password dimenticata?',
    createAccount: 'Crea account',
    haveAccount: 'Hai già un account?',
    noAccount: 'Non hai un account?',
    backToSignIn: 'Torna all’accesso',
    verifyTitle: 'Conferma l’email',
    verifySent: (email: string) => `Email inviata a ${email}`,
    verifySentAnywhere: 'Abbiamo inviato un’email con il link al tuo indirizzo',
    verifyHint:
      'Apri il link nell’email per confermare l’indirizzo: lì arrivano le email di recupero della password. Se non la vedi, controlla la cartella «Spam».',
    verifyEmailPrompt: 'Indica l’email con cui è registrato l’account',
    verifyResend: 'Invia di nuovo l’email',
    verifyResendIn: (seconds: Count) => `Potrai inviarla di nuovo tra ${seconds.text} s`,
    verifyResent: 'Abbiamo inviato un’altra email',
    verifyResentMaybe: 'Se l’indirizzo è registrato e non ancora confermato, una nuova email è in arrivo',
    verifyChecking: 'Verifica del link',
    verifyDone: 'Email confermata',
    verifyDoneHint: 'Apertura dell’app',
    verifyDoneSignInHint: 'Apertura dell’accesso',
    resetTitle: 'Recupero password',
    resetSubtitle: 'Indica l’email: ti invieremo un link per cambiare la password',
    resetSend: 'Invia il link',
    resetSent: 'Se l’account esiste, l’email è già in arrivo',
    resetSentHint:
      'Non mostriamo se un indirizzo è registrato: altrimenti questo modulo permetterebbe di verificare le email altrui. Controlla «Posta in arrivo» e «Spam».',
    newPassword: 'Nuova password',
    newPasswordSubtitle: 'Scegli una nuova password. Quella vecchia smetterà subito di funzionare.',
    newPasswordSubmit: 'Salva la password',
    requestNewLink: 'Richiedi un nuovo link',
    newPasswordDone: 'Password aggiornata',
    newPasswordDoneHint: 'Apertura dell’accesso',
    signOut: 'Esci',
    totpCode: 'Codice di conferma',
    totpHint: 'Le sei cifre dell’app di autenticazione',
    recoveryCode: 'Codice di recupero',
    recoveryCodeHint: 'Otto caratteri dai codici salvati quando hai attivato la 2FA',
    useRecoveryCode: 'Usa un codice di recupero',
    useAppCode: 'Usa l’app di autenticazione',
    resetLinkInvalid: 'Il link non funziona',
    errors: {
      invalidCredentials: 'Email o password errate',
      emailTaken: 'Questa email è già registrata',
      weakPassword: 'La password è troppo corta',
      passwordRule: 'La password deve contenere lettere e numeri',
      passwordsDiffer: 'Le password non coincidono',
      invalidEmail: 'Inserisci un indirizzo nel formato nome@esempio.com',
      invalidReferralCode: 'Controlla il codice invito',
      referralRequired: 'Inserisci il codice invito',
      emailRequired: 'Indica l’email',
      passwordRequired: 'Inserisci la password',
      accountBlocked: 'L’accesso all’account è limitato. Scrivi all’assistenza',
      rateLimited: 'Troppi tentativi. Riprova più tardi',
      totpRequired: 'Inserisci il codice dell’app di autenticazione',
      totpInvalid: 'Codice non valido. Controlla l’ora del telefono e inseriscine uno nuovo',
      totpFormat: 'Il codice dell’app è di sei cifre, un codice di recupero di otto caratteri',
      expiredToken: 'Il link è scaduto. Richiedine uno nuovo',
      verifyLinkUsed: 'Questo link è già stato usato. Accedi, oppure apri l’email più recente se ne hai richiesta una nuova',
      missingToken: 'Il link è incompleto. Aprilo per intero dall’email',
      validation: 'Controlla i campi compilati',
      network: 'Nessuna connessione al server. Controlla la rete',
    },
  },

  home: {
    promoLabel: 'Promozioni',
    banners: [
      {
        title: 'Segnali sull’oro',
        subtitle: 'Un codice dal nostro canale Telegram: un’operazione in un solo passo',
        cta: 'Inserisci il codice',
      },
      {
        title: 'Mercati in tempo reale',
        subtitle: 'Grafici e book in diretta per tutte le coppie',
        cta: 'Apri i mercati',
      },
      {
        title: 'Deposita in pochi minuti',
        subtitle: 'USDT sulle reti TRC20 ed ERC20',
        cta: 'Ricarica il conto',
      },
    ],
    pauseAnnouncements: 'Metti in pausa gli annunci',
    resumeAnnouncements: 'Riprendi gli annunci',
    announcementsLabel: 'Annunci',
    announcements: [
      'Prima di depositare, controlla valuta e rete del trasferimento',
      'Gestisci la protezione dell’account nella sezione «Sicurezza»',
      'La cronologia delle operazioni è nella sezione «Portafoglio»',
    ],
    totalBalance: 'Saldo totale',
    quickTrade: 'Trading rapido',
    quickTradeHint: 'Spot e futures in un unico terminale',
    quickActions: 'Azioni rapide',
    signalLive: 'Segnale disponibile',
    signalNext: 'Prossimo segnale',
    signalClosesIn: 'si chiude tra',
    signalOpensAt: (time: string) => `si apre ${atHour(time)}`,
    signalsTitle: 'Segnali sull’oro',
    allSignals: 'Tutti i segnali',
    marketMoves: 'Andamento del mercato',
    pair: 'Coppia',
    lastPrice: 'Prezzo',
    change24h: 'Variazione 24 h',
    /** After the figure in the home balance's change pill: « +1.30% in 24 h ». */
    change24hShort: 'in 24 h',
    volume24h: 'Volume 24 h',
    allMarkets: 'Tutti i mercati',
    pricesUnavailable: 'Quotazioni non disponibili: impossibile valutare l’intero portafoglio',
    ratesUnavailable: 'Quotazioni temporaneamente non disponibili',
    marketsUnavailable: 'L’elenco dei mercati è temporaneamente non disponibile',
    signalsNone: 'Nessun segnale al momento: i nuovi codici escono sul nostro canale Telegram',
  },

  markets: {
    title: 'Mercati',
    search: 'Cerca una coppia',
    spot: 'Spot',
    futures: 'Futures',
    gainers: 'Rialzi',
    losers: 'Ribassi',
    volume: 'Volume 24 h',
    volumeShort: 'volume',
    typeLabel: 'Tipo di mercato',
    sortLabel: 'Ordinamento',
    clearSearch: 'Cancella la ricerca',
    favourites: 'Preferiti',
    addFavourite: 'Aggiungi ai preferiti',
    removeFavourite: 'Rimuovi dai preferiti',
    emptyFavourites: 'Nessun preferito: segna le coppie con la stella',
    empty: 'Nessun risultato',
    emptyHint: 'Controlla il simbolo della coppia, ad esempio BTC o SOL.',
    emptySpot: 'Nessuna coppia spot ancora aperta',
    emptyFutures: 'Nessuna coppia futures ancora aperta',
    loadFailed: 'Impossibile caricare l’elenco dei mercati',
    pricesUnavailable: 'Quotazioni temporaneamente non disponibili. I prezzi si aggiorneranno automaticamente.',
  },
  assets: {
    /** Asset names the interface translates; any other asset keeps the name the server sends. */
    names: { XAU: 'Oro' } as Record<string, string>,
  },

  wallet: {
    title: 'Portafoglio',
    totalValue: 'Valore totale del conto',
    accounts: 'I miei conti',
    latestWithdrawal: 'Ultimo prelievo',
    coin: 'Moneta',
    searchCoin: 'Cerca una moneta',
    quickActions: 'Azioni rapide',
    coins: 'Criptovalute',
    hideBalance: 'Nascondi il saldo',
    showBalance: 'Mostra il saldo',
    loadError: 'Impossibile caricare il saldo',
    pricesUnavailable: 'Quotazioni non disponibili: impossibile valutare l’intero portafoglio',
    coinsEmpty: 'Nessuna criptovaluta per ora. Ricarica il conto e appariranno qui.',
    accountSpot: 'Conto spot',
    accountSpotHint: 'Disponibile per il trading',
    accountFutures: 'Conto futures',
    accountFuturesHint: 'Margine delle posizioni',
    accountCopy: 'Copy trading (archivio)',
    accountCopyHint: 'Trasferiscilo sullo spot',
    actions: {
      deposit: 'Deposita',
      withdraw: 'Preleva',
      transfer: 'Trasferisci',
      history: 'Cronologia',
    },
    deposit: {
      title: 'Deposito',
      history: 'Cronologia dei depositi',
      chain: 'Rete',
      saveQr: 'Salva il codice QR',
      address: 'Indirizzo di deposito',
      copyAddress: 'Copia l’indirizzo',
      notice: 'Avviso importante',
      noticeWrongNetwork: (asset: string, network: string) =>
        `Invia solo ${asset} sulla rete ${network}. I fondi inviati da un’altra rete andranno persi.`,
      noticeConfirmations: (count: Count, minutes: Count) =>
        `Accredito dopo ${count.text} ${pluralEn(count, 'conferma', 'conferme')} della rete, di solito in circa ${minutes.text} ${pluralEn(minutes, 'minuto', 'minuti')}.`,
      noticeMinimum: (amount: string, asset: string) =>
        `Deposito minimo: ${amount} ${asset}.`,
      waiting: 'In attesa del trasferimento',
      incoming: 'Depositi in arrivo',
      incomingFirst: 'Visto in rete, in attesa della prima conferma',
      incomingProgress: (done: Count, total: Count) => `Conferme: ${done.text} su ${total.text}`,
      selectAsset: 'Scegli una valuta',
      noAssets: 'Il deposito è temporaneamente non disponibile. Riprova più tardi.',
      noNetworks: 'Nessuna rete disponibile per questa valuta: scegline un’altra.',
      addressFailed: 'Impossibile ottenere l’indirizzo di deposito',
      noticePermanent: 'L’indirizzo è permanente: usalo per tutti i prossimi depositi.',
      qrSaved: 'Codice QR salvato',
      qrFailed: 'Impossibile salvare il codice QR',
      paymentLink: 'Link per il wallet',
      paymentLinkHint: 'Aprilo nel wallet: rete e indirizzo si compilano da soli.',
      copyLink: 'Copia il link',
    },
    withdraw: {
      title: 'Prelievo',
      history: 'Cronologia dei prelievi',
      asset: 'Valuta del prelievo',
      selectAsset: 'Scegli una valuta',
      network: 'Rete del prelievo',
      address: 'Indirizzo di prelievo',
      addressPlaceholder: (network: string) => `Inserisci un indirizzo ${network}`,
      addressPaste: 'Incolla l’indirizzo',
      addressFromBook: (label: string): string => `Confermato nella rubrica degli indirizzi · ${label}`,
      paste: 'Incolla',
      addressInvalid: (network: string) => `Non sembra un indirizzo della rete ${network}`,
      addressWrongNetwork: (detected: string, selected: string) =>
        `È un indirizzo della rete ${detected}, ma è selezionata ${selected}`,
      amount: 'Importo del prelievo',
      amountPlaceholder: '0.00',
      networkFee: 'Commissione di rete',
      platformFee: (rate: string): string => `Commissione della piattaforma (${rate}%)`,
      feeFree: 'al momento senza commissione',
      feeDynamic: 'al prezzo attuale del gas',
      willReceive: 'Riceverai',
      notice: 'Avviso importante',
      noticeReview: 'Ogni richiesta viene verificata prima dell’invio. I fondi vengono riservati subito.',
      noticeTiming: 'Invio entro 24 ore dall’approvazione.',
      noticeMinimum: (amount: string, asset: string) =>
        `Prelievo minimo: ${amount} ${asset}.`,
      noticeSupport:
        'Controlla tu stesso l’indirizzo: un trasferimento a un indirizzo sbagliato non può essere recuperato. L’assistenza esamina le contestazioni in base al numero della richiesta.',
      insufficient: 'Fondi insufficienti',
      noticeLimit: (remaining: string, limit: string, verified: boolean): string =>
        verified ? `Rimasti oggi: ${remaining} su ${limit} USDT.` : `Senza verifica: fino a ${limit} USDT al giorno, rimasti ${remaining}. Completa la verifica per alzare il limite.`,
      addressNotConfirmed: 'Prelievi solo verso indirizzi della rubrica confermati via email',
      limitReached: (verified: boolean): string => (verified ? 'Limite giornaliero di prelievo esaurito' : 'Limite giornaliero senza verifica esaurito: completa la verifica nel profilo'),
      belowMinimum: (amount: string, asset: string) => `Minimo ${amount} ${asset}`,
      confirmTitle: 'Controlla la richiesta',
      confirmSubtitle: 'Un prelievo inviato non può essere annullato. Controlla la rete e l’indirizzo.',
      confirmAction: 'Conferma il prelievo',
      failed: 'Impossibile creare la richiesta',
      submitted: 'Richiesta creata',
      submittedHint: 'Dopo la verifica, i fondi partono sulla rete. Segui lo stato nelle richieste di prelievo.',
      requestId: 'Numero della richiesta',
      statusPending: 'In verifica',
      toRequests: 'Alle richieste di prelievo',
      empty: 'Al momento nessuna valuta è disponibile per il prelievo',
      loadFailed: 'Impossibile caricare l’elenco delle valute',
      routeUnavailable: 'I prelievi su questa rete non sono disponibili al momento — scegli un’altra rete',
      invalidAmount: 'L’importo non va bene: controlla la precisione e la commissione di rete',
      edit: 'Correggi la richiesta',
    },
    withdrawals: {
      title: 'Richieste di prelievo',
      toAddress: (address: string): string => `a ${address}`,
      note: 'Le richieste rifiutate o non riuscite restituiscono la riserva al conto. Apri una richiesta per vederne la transazione o il motivo.',
      empty: 'Nessuna richiesta di prelievo per ora. Ogni richiesta mostrerà qui il suo stato, dalla verifica alla conferma della rete.',
      loadError: 'Impossibile caricare le richieste',
      details: 'Richiesta di prelievo',
      created: 'Creata',
      transaction: 'Transazione',
      openExplorer: 'Apri nell’explorer',
      reason: 'Motivo',
      status: {
        pending: 'In verifica',
        approved: 'Approvata',
        rejected: 'Rifiutata',
        broadcasting: 'Invio in corso',
        sent: 'Inviata',
        confirmed: 'Confermata',
        failed: 'Non riuscita',
      },
      statusHint: {
        pending: 'La richiesta è in verifica. L’importo è riservato sul conto.',
        approved: 'Verificata, in attesa di invio sulla rete.',
        rejected: 'La richiesta è stata rifiutata; la riserva è tornata sul conto.',
        broadcasting: 'La transazione è in invio sulla rete.',
        sent: 'La transazione è sulla rete, in attesa di conferma.',
        confirmed: 'La rete ha confermato la transazione. I fondi sono arrivati.',
        failed: 'L’invio non è riuscito; la riserva è tornata sul conto.',
      },
    },
    transfer: {
      title: 'Trasferimento',
      from: 'Da',
      fromAvailable: (amount: string): string => `Da · ${amount} disponibili`,
      to: 'A',
      submit: 'Trasferisci',
      swap: 'Inverti i conti',
      selectFrom: 'Conto di addebito',
      selectTo: 'Conto di accredito',
      selectAsset: 'Scegli una valuta',
      after: 'Dopo il trasferimento',
      insufficient: 'Fondi insufficienti sul conto di addebito',
      done: 'Trasferimento eseguito',
      hint: 'I trasferimenti tra i tuoi conti sono istantanei e senza commissioni.',
      empty: 'Non c’è ancora nulla da trasferire: prima ricarica il conto.',
      failed: 'Impossibile eseguire il trasferimento. Riprova',
    },
    history: {
      title: 'Cronologia operazioni',
      deposit: 'Deposito',
      withdrawal: 'Prelievo',
      trade: 'Operazione',
      fee: 'Commissione',
      transfer: 'Trasferimento',
      pnl: 'Risultato',
      empty: 'Nessuna operazione per ora. Qui appariranno depositi, prelievi e operazioni.',
      emptyFiltered: 'Nessuna operazione per questo filtro',
      loadError: 'Impossibile caricare la cronologia',
      today: 'Oggi',
      yesterday: 'Ieri',
      balanceAfter: (amount: string) => `saldo ${amount}`,
      onlyAccount: (account: string) => `Solo ${account}`,
      resetAccount: 'Mostra tutti i conti',
      accountFilter: 'Conto',
      allAccounts: 'Tutti i conti',
      withdrawalsLink: 'Stato delle richieste di prelievo',
      badges: {
        credited: 'Accreditato',
        sent: 'Inviato',
        fee: 'Commissione',
        reserved: 'Riservato',
        returned: 'Restituito',
        released: 'Liberato',
        filled: 'Eseguito',
        settled: 'Regolato',
        liquidated: 'Liquidato',
        done: 'Completato',
        adjusted: 'Rettificato',
        paid: 'Pagato',
      },
      filters: {
        all: 'Tutte',
        rewards: 'Bonus',
        deposits: 'Depositi',
        withdrawals: 'Prelievi',
        trades: 'Operazioni',
        transfers: 'Trasferimenti',
      },
      types: {
        deposit: 'Deposito',
        withdrawal: 'Prelievo',
        withdrawal_fee: 'Commissione di prelievo',
        withdrawal_platform_fee: 'Commissione della piattaforma sul prelievo',
        withdrawal_lock: 'Riserva per prelievo',
        withdrawal_release: 'Rilascio riserva di prelievo',
        order_lock: 'Riserva per ordine limite',
        order_release: 'Rilascio riserva dell’ordine',
        bonus: 'Bonus promozionale',
        trade_buy: 'Acquisto',
        trade_sell: 'Vendita',
        trading_fee: 'Commissione di trading',
        realized_pnl: 'Risultato dell’operazione',
        signal_programmed_profit: 'Profitto programmato del segnale',
        funding: 'Finanziamento',
        margin_lock: 'Riserva di margine',
        margin_release: 'Rilascio del margine',
        liquidation: 'Liquidazione',
        transfer_in: 'Trasferimento in entrata',
        transfer_out: 'Trasferimento in uscita',
        copy_allocation: 'Allocato alla copia',
        copy_release: 'Rientro dalla copia',
        copy_commission: 'Commissione di copy trading',
        referral: 'Ricompensa referral',
        adjustment: 'Rettifica',
      },
    },
  },

  trade: {
    title: 'Trading',
    menu: {
      account: 'Conto',
      trading: 'Trading',
      more: 'Altro',
    },
    long: 'Long',
    short: 'Short',
    buy: 'Compra',
    sell: 'Vendi',
    marketOrder: 'A mercato',
    limitOrder: 'Limite',
    price: 'Prezzo',
    marketPrice: 'A mercato',
    quantity: 'Quantità',
    leverage: 'Leva',
    takeProfitStopLoss: 'Take profit / stop loss',
    positions: 'Posizioni',
    orders: 'Ordini',
    history: 'Cronologia',
    copyTrading: 'Copia operazioni',
    unrealizedPnl: 'P&L non realizzato',
    unrealizedPnlShort: 'P&L',
    margin: 'Margine',
    entry: 'Ingresso',
    size: 'Dimensione',
    liquidation: 'Liquidazione',
    close: 'Chiudi',
    orderBook: 'Book di negoziazione',
    noPositions: 'Inserisci un ordine qui sopra e la posizione apparirà qui.',
    balance: 'Saldo',
    perpetual: 'Perpetuo',
    perpShort: 'Perp',
    fundingIn: (time: string) => `tra ${time}`,
    position: 'Posizione',
    closeAtMarket: 'Chiudi a mercato',
    keepPosition: 'Mantieni la posizione',
    placeBuyOrder: 'Invia ordine di acquisto',
    placeSellOrder: 'Invia ordine di vendita',
    placeLongOrder: 'Invia ordine long',
    placeShortOrder: 'Invia ordine short',
    isolatedMargin: 'Margine isolato',
    crossMargin: 'Margine cross',
    markShort: 'Mark',
    liqPrice: 'Prezzo liq.',
    noPositionsTitle: 'Nessuna posizione aperta',
    noOrdersTitle: 'Nessun ordine attivo',
    noHistoryTitle: 'Nessuna operazione per ora',
    pair: 'Coppia',
    selectPair: 'Scegli una coppia',
    sideLabel: 'Direzione',
    orderType: 'Tipo di ordine',
    sizeShare: 'Quota del disponibile',
    quantityShort: 'Qtà',
    takeProfit: 'Take profit',
    stopLoss: 'Stop loss',
    tabsLabel: 'Le mie operazioni',
    openChart: 'Grafico',
    openLong: (symbol: string) => `Long ${symbol}`,
    openShort: (symbol: string) => `Short ${symbol}`,
    buyAsset: (symbol: string) => `Compra ${symbol}`,
    sellAsset: (symbol: string) => `Vendi ${symbol}`,
    enterQuantity: 'Indica la quantità',
    insufficient: 'Fondi insufficienti sul conto',
    belowMinNotional: (amount: string, asset: string) =>
      `Volume minimo dell’ordine: ${amount} ${asset}`,
    bookEmpty: 'Nessun ordine nel book per ora',
    bookUnavailable: 'Book non disponibile',
    tradingUnavailable:
      'Il servizio di trading non risponde: al momento non è possibile inviare l’ordine. Saldi, depositi e prelievi funzionano regolarmente.',
    marketsFailed: 'Impossibile caricare l’elenco delle coppie',
    noMarkets: 'Nessuna coppia per ora: appariranno con l’apertura dei mercati.',
    confirmTitle: 'Controlla l’ordine',
    confirmSubtitle: 'Un ordine eseguito non può essere annullato.',
    marginRequired: 'Margine utilizzato',
    cost: 'Da pagare',
    liquidationEstimate: 'Liquidazione stimata',
    submitted: 'Ordine inviato',
    submitFailed: 'Impossibile inviare l’ordine',
    futuresSoon: 'I futures sono temporaneamente non disponibili. Il trading spot funziona.',
    marginMode: 'Margine',
    cross: 'Cross',
    isolated: 'Isolato',
    marginModeHint: (mode: string): string =>
      mode === 'cross'
        ? 'Cross: l’intero saldo futures sostiene la posizione'
        : 'Isolato: rischia solo il margine di questa posizione',
    reduceOnly: 'Solo riduzione',
    futuresAccount: 'Conto futures',
    equity: 'Capitale',
    marginUsed: 'In margine',
    marginRatio: 'Livello di rischio',
    funding: 'Finanziamento',
    fundingRate: 'Tasso per 8 h',
    nextFunding: 'Prossimo',
    fundingPaid: 'Finanziamento',
    protection: 'Protezione',
    editProtection: 'TP / SL',
    protectionSheetTitle: 'Take profit e stop loss',
    protectionSheetSubtitle: 'Il motore chiuderà la posizione a prezzo di mercato non appena il prezzo toccherà il livello.',
    clearLevel: 'Nessun livello',
    addMargin: 'Aggiungi margine',
    removeMargin: 'Rimuovi margine',
    marginDelta: 'Importo del margine',
    marginDeltaHint: 'Posizione isolata: più margine allontana la liquidazione',
    addMarginKyc: 'L’aggiunta di margine si apre una volta approvati i documenti. Puoi già rimuovere margine, modificare take profit e stop loss o chiudere la posizione.',
    saveProtection: 'Salva',
    protectionSaved: 'Protezione aggiornata',
    protectionFailed: 'Impossibile aggiornare la posizione',
    protectionInvalid: 'Il livello è dal lato sbagliato del prezzo di ingresso',
    leverageTooHigh: 'La leva supera il massimo consentito per questa coppia',
    leverageTier: (cap: number, notional: string) => `Per un volume di ${notional} USDT, leva fino a ${cap}×`,
    futuresDisabled: 'Gli ordini futures non vengono accettati temporaneamente',
    liquidated: 'Liquidata',
    restingToast: (symbol: string, price: string) => `Ordine limite ${symbol} a ${price} inserito, in attesa del prezzo`,
    cancelledToast: 'Ordine annullato, fondi restituiti',
    costSell: 'Da ricevere',
    costBasis: 'Costo',
    enterProtection: 'Il livello di take profit o stop loss non è un prezzo valido',
    leverageLocked: (leverage: number) => `La leva è fissata a ${leverage}× dalla posizione aperta. Chiudila per cambiarla`,
    marginReleased: 'Margine restituito',
    realizedEstimate: 'Risultato stimato',
    addsPosition: (quantity: string, asset: string) => `Aggiunge ${quantity} ${asset} alla posizione aperta`,
    reducesPosition: (quantity: string, asset: string) => `Riduce la posizione aperta di ${quantity} ${asset}`,
    closesPosition: (quantity: string, asset: string) => `Chiude la posizione aperta di ${quantity} ${asset}`,
    reversesPosition: (closeQuantity: string, openQuantity: string, asset: string) =>
      `Chiude la posizione di ${closeQuantity} ${asset} e apre ${openQuantity} ${asset} nella direzione opposta`,
    byAccount: 'su tutto il conto',
    cancelledToastPlain: 'Ordine annullato',
    fillBuy: 'Acquisto',
    fillSell: 'Vendita',
    marginFloor: (floor: string) => `Il margine non può scendere sotto il margine iniziale di ${floor} USDT`,
    marginRemovable: (amount: string) => `Si possono rimuovere fino a ${amount} USDT`,
    enterPrice: 'Indica il prezzo dell’ordine limite',
    markPrice: 'Prezzo mark',
    tpShort: 'TP',
    slShort: 'SL',
    priceStale: 'Il prezzo non è aggiornato: il flusso delle quotazioni è in ritardo. Riprova tra qualche secondo.',
    marketDisabled: 'Le negoziazioni su questa coppia sono sospese',
    filledToast: (side: string, quantity: string, asset: string, price: string) =>
      `Ordine eseguito: ${side.toLocaleLowerCase('it-IT')} ${quantity} ${asset} a ${price}`,
    closedToast: (symbol: string, pnl: string) => `${symbol} chiusa, risultato ${pnl} USDT`,
    positionsFailed: 'Impossibile caricare le posizioni',
    noOrders: 'Un ordine limite attenderà qui fino all’esecuzione.',
    ordersFailed: 'Impossibile caricare gli ordini',
    noHistory: 'La prima operazione eseguita apparirà in questo elenco.',
    historyFailed: 'Impossibile caricare la cronologia delle operazioni',
    cancelOrder: 'Annulla',
    cancelFailed: 'Impossibile annullare l’ordine',
    closeFailed: 'Impossibile chiudere la posizione',
    closeConfirmTitle: 'Chiudere la posizione?',
    closeConfirmSubtitle:
      'La posizione verrà chiusa a prezzo di mercato e il risultato attuale diventerà definitivo. L’operazione non è reversibile.',
  },

  chart: {
    title: 'Grafico',
    interval: 'Intervallo',
    chartType: 'Tipo di grafico',
    candles: 'Candele',
    line: 'Linea',
    high24h: 'Max 24 h',
    low24h: 'Min 24 h',
    volume24h: 'Volume 24 h',
    empty: 'Il grafico apparirà quando ci saranno abbastanza dati per questa coppia',
    failed: 'Impossibile caricare il grafico',
    unknownPair: 'Questa coppia non esiste: scegline una nella scheda «Mercati»',
    toMarkets: 'Ai mercati',
  },

  security: {
    title: 'Sicurezza',
    subtitle: 'Il secondo fattore protegge l’accesso e i prelievi',
    statusOn: 'Attivo',
    statusOff: 'Non attivo',
    explain:
      'Il codice dell’app di autenticazione viene richiesto all’accesso e a ogni richiesta di prelievo. Senza di esso l’account è protetto solo dalla password.',
    setup: 'Configura l’autenticatore',
    step1: 'Scansiona il codice in Google Authenticator, 1Password o Aegis',
    secretLabel: 'Oppure inserisci il segreto manualmente',
    step2: 'Inserisci il codice dell’app',
    enable: 'Attiva',
    enabled: 'Secondo fattore attivato',
    disableTitle: 'Disattiva il secondo fattore',
    disableHint: 'Inserisci il codice attuale per confermare che il telefono è nelle tue mani',
    disable: 'Disattiva',
    disabled: 'Secondo fattore disattivato',
    codeInvalid: 'Codice non valido. Controlla l’ora del telefono e inseriscine uno nuovo',
    failed: 'Impossibile modificare le impostazioni di sicurezza',
    withdrawalCode: 'Codice di conferma',
    withdrawalCodeHint: 'Il prelievo è protetto dal secondo fattore: inserisci il codice dell’app',
    recoveryTitle: 'Codici di recupero',
    recoveryIntro: 'Conserva questi codici in un luogo sicuro. Ciascuno consente un accesso o un prelievo se il telefono non è disponibile. Non verranno più mostrati.',
    recoveryCopy: 'Copia i codici',
    recoveryCopied: 'Codici copiati',
    recoverySaved: 'Ho salvato i codici',
    recoveryLeft: (count: number): string => `Codici di recupero rimasti: ${count}`,
    recoveryRegenerate: 'Nuovi codici di recupero',
    recoveryRegenerateHint: 'I vecchi codici smetteranno di funzionare. Inserisci il codice dell’app',
    recoveryRegenerated: 'Sono stati emessi nuovi codici di recupero',
    codeOrRecovery: 'Le sei cifre dell’app o un codice di recupero',
  },

  addressBook: {
    title: 'Indirizzi di prelievo',
    hint: 'Un nuovo indirizzo diventa utilizzabile dopo la conferma tramite il link nell’email. Così una sessione estranea non può dirottare i fondi su un proprio indirizzo.',
    add: 'Aggiungi un indirizzo',
    address: 'Indirizzo',
    label: 'Nome',
    labelPlaceholder: 'Ad esempio, Ledger',
    send: 'Invia l’email di conferma',
    saved: 'Indirizzi salvati',
    empty: 'Nessun indirizzo per ora',
    emptyBody: 'Aggiungi l’indirizzo su cui prelevi: una volta confermato dall’email, lo scegli con un tocco nel modulo di prelievo.',
    statusConfirmed: 'confermato',
    statusPending: 'in attesa di conferma via email',
    resend: 'Invia di nuovo',
    resendLink: 'Invia di nuovo l’email di conferma',
    remove: 'Elimina l’indirizzo',
    removeTitle: 'Eliminare questo indirizzo?',
    removeHint: 'I prelievi verso questo indirizzo non saranno possibili finché non lo aggiungi e confermi di nuovo.',
    linkSent: 'Email inviata: apri il link per confermare l’indirizzo',
    alreadyConfirmed: 'Questo indirizzo è già confermato',
    invalid: 'L’indirizzo non corrisponde alla rete scelta',
    confirmed: (short: string): string => `Indirizzo ${short} confermato`,
    confirmFailed: 'Il link non è valido o è scaduto',
  },

  sessions: {
    title: 'Sessioni e dispositivi',
    subtitle: 'Dove è stato effettuato l’accesso al tuo account',
    current: 'Questo dispositivo',
    started: 'Accesso',
    lastSeen: 'Attività',
    end: 'Termina',
    endOthers: 'Termina tutte le altre sessioni',
    ended: 'Sessione terminata',
    endedOthers: (count: number): string => `Sessioni terminate: ${count}`,
    empty: 'Nessun’altra sessione attiva',
    failed: 'Impossibile caricare le sessioni',
    hint: 'Una sessione terminata perde l’accesso entro quindici minuti: è la durata di una chiave di accesso già emessa.',
  },

  profile: {
    title: 'Profilo',
    accountId: 'ID account',
    idCopied: 'ID copiato',
    loadFailed: 'Impossibile caricare il profilo',
    partnerProgram: 'Programma partner',
    partnerProgramHint: 'Team, livello e ricompense',
    securityOn: 'Secondo fattore attivo',
    securityOff: 'Attiva il secondo fattore',

    verification: 'Verifica',
    verificationNone: 'Non effettuata',
    verificationPending: 'In verifica',
    verificationInReview: 'In verifica',
    verificationApproved: 'Completata',
    verificationRejected: 'Rifiutata',
    verificationResubmit: 'Servono nuovi documenti',
    verificationCta: 'Inizia',
    verificationCtaAgain: 'Invia di nuovo',
    verificationPrompt: 'Carica il passaporto: bastano 2 minuti',
    verificationPendingHint: 'L’operatore di solito esamina i documenti entro un giorno.',
    verificationRejectedHint: 'L’operatore non ha accettato i documenti. Puoi inviare di nuovo la richiesta.',
    verificationOpen: 'Apri la richiesta',

    withdrawalAddressesHint: 'Rubrica con conferma via email',
    withdrawalAddresses: 'Indirizzi di prelievo',
    addressesSubtitle: 'Indirizzi verso cui hai già prelevato fondi',
    addressesEmpty: 'Non hai ancora prelevato fondi. L’indirizzo della prima richiesta apparirà qui.',
    addressesFailed: 'Impossibile caricare gli indirizzi',
    addressCopied: 'Indirizzo copiato',

    security: 'Sicurezza',
    accountAccess: 'Email e password',
    sessions: 'Sessioni e dispositivi',
    sessionsHint: 'Dove hai effettuato l’accesso',
    securitySubtitle: 'Accesso all’account',
    securityEmailVerified: 'Email confermata',
    securityEmailUnverified: 'Email non confermata',
    securityPassword: 'Cambia password',
    securityPasswordHint: 'Ti invieremo via email un link per cambiare la password',

    notifications: 'Notifiche',
    pushOn: 'Push attive',
    pushOff: 'Push disattivate',
    pushToggle: 'Notifiche push',
    pushUnsupported: 'Questo browser non supporta le notifiche push',
    pushDenied: 'Le notifiche sono bloccate nelle impostazioni del browser',
    pushFailed: 'Impossibile attivare le notifiche push',
    pushUnavailable: 'Le notifiche push non sono ancora configurate sul server',


    theme: 'Tema',
    themeHint: 'Il tema nero è quello del brand e risparmia batteria su OLED. Quello chiaro è per il sole intenso. «Come il sistema» segue l’impostazione del telefono.',
    themeNames: { system: 'Come il sistema', dark: 'Scuro', light: 'Chiaro' } as Record<'system' | 'dark' | 'light', string>,
    themeSystemHint: 'Segue l’impostazione del dispositivo',
    language: 'Lingua',
    languageHint: 'Lingua dell’interfaccia. Gli importi mantengono un formato unico; le date seguono la lingua.',

    support: 'Assistenza',
    supportTitle: 'Contatta il supporto',
    supportChoose: 'Scegli come preferisci scriverci',
    supportVia: 'Telegram o e-mail',
    supportTelegram: 'Scrivi su Telegram',
    supportEmail: 'Scrivi via e-mail',
    about: 'Informazioni sull’app',
    version: 'Versione',

    signOut: 'Esci',
    signOutTitle: 'Uscire dall’account?',
    signOutBody:
      'Saldi, posizioni aperte e storico dei segnali resteranno al loro posto: li ritroverai al prossimo accesso. Gli altri dispositivi restano connessi.',

    kyc: {
      title: 'Verifica',
      intro:
        'Solo l’operatore addetto alla verifica vede questi dati. Il numero del documento è conservato cifrato e non viene mai mostrato per intero.',
      loadFailed: 'Impossibile caricare la richiesta',

      document: 'Documento',
      documentType: 'Tipo di documento',
      typePassport: 'Passaporto',
      typeIdCard: 'Carta d’identità',
      typeDriverLicense: 'Patente di guida',
      number: 'Numero del documento',
      numberPlaceholder: '4510 123456',
      numberHint: 'Serie e numero, come riportati sul documento',
      country: 'Paese di rilascio',
      countryPlaceholder: 'Scegli un paese',
      issueDate: 'Data di rilascio',
      expiryDate: 'Valido fino al',

      personal: 'Titolare del documento',
      lastName: 'Cognome',
      firstName: 'Nome',
      dateOfBirth: 'Data di nascita',

      documents: 'Foto del documento',
      docMain: 'Pagina principale',
      docMainHint: 'La pagina con foto e numero',
      docFront: 'Fronte',
      docFrontHint: 'Il lato con la foto e il numero',
      choose: 'Scegli un file',
      replace: 'Sostituisci',
      remove: 'Rimuovi',
      fileHint: (size: string) => `JPG, PNG o PDF, fino a ${size} MB`,
      megabytes: 'MB',

      submit: 'Invia per la verifica',
      submitFailed: 'Impossibile inviare la richiesta. Controlla i dati e riprova',
      uploadFailed:
        'I dati sono stati inviati, ma non è stato possibile caricare i documenti. Prova ad allegarli di nuovo.',
      uploadRetry: 'Carica i documenti',
      submitted: 'Richiesta inviata',
      submittedHint: 'Un operatore verificherà i documenti e aggiornerà lo stato.',

      review: 'Richiesta in verifica',
      reviewHint: 'Durante la verifica i dati non possono essere modificati.',
      approved: 'Verifica completata',
      approvedHint: 'L’operatore ha confermato i documenti.',
      rejected: 'Richiesta rifiutata',
      rejectedHint: 'Correggi i dati e invia di nuovo la richiesta.',
      resubmit: 'Servono nuovi documenti',
      resubmitHint: 'Carica un documento valido e invia di nuovo la richiesta.',
      rejectionReason: 'Motivo',
      submittedAt: (date: string) => `Inviata il ${date}`,
      attached: 'Documenti allegati',
      attachedEmpty: 'Nessun file allegato alla richiesta',
      startNew: 'Invia di nuovo',
      step: (current: number, total: number): string => `Passo ${current} di ${total}`,

      gate: {

        title: 'Verifica la tua identità per usare i fondi',

        body: 'Puoi depositare subito. Trading, segnali, trasferimenti e prelievi si aprono una volta approvati i documenti, di solito entro un giorno.',

        pending: 'I documenti sono in verifica: l’accesso si apre con l’approvazione, di solito entro un giorno.',

        action: 'Verifica l’identità',

      },

      kinds: {
        passport_main: 'Pagina principale',
        passport_registration: 'Pagina della residenza',
        selfie_with_document: 'Selfie con il documento',
        proof_of_address: 'Prova di residenza',
      },

      errors: {
        required: 'Compila il campo',
        number: 'Solo lettere latine, cifre, spazi e trattini',
        name: 'Solo lettere, spazi, apostrofi e trattini',
        country: 'Scegli un paese',
        date: 'Indica una data',
        issueFuture: 'La data di rilascio non può essere futura',
        expiryPast: 'Il documento è scaduto',
        expiryBeforeIssue: 'La data di scadenza deve essere successiva a quella di rilascio',
        age: 'La verifica è disponibile dai 18 anni',
        birthAfterIssue: 'La data di nascita deve precedere la data di rilascio',
        fileType: 'Va bene un file JPG, PNG o PDF',
        fileSize: (size: string) => `Il file supera ${size} MB`,
        documentsRequired: 'Allega una foto o una scansione del documento',
        fileMissing: 'Aggiungi questo file',
      },
    },
  },

  countries: {
    search: 'Nome o codice del paese',
    popular: 'Più frequenti',
    all: 'Tutti i paesi',
    nothing: 'Nessun risultato. Prova il nome in inglese o un codice, ad esempio DE',
    found: (count: number): string => `Paesi trovati: ${count}`,
  },

  notifications: {
    promoUnlocked: (amount: string): string => `Bonus promozionale di ${amount} USDT sbloccato e accreditato su spot`,
    promoExpired: 'Il bonus promozionale è scaduto: le condizioni non sono state soddisfatte in tempo',
    referralQualified: (name: string, signals: Count): string =>
      signals.value.greaterThan(0)
        ? `Il tuo invitato ${name} si è qualificato: +${signals.text} ${pluralEn(signals, 'segnale bonus', 'segnali bonus')}`
        : `Il tuo invitato ${name} si è qualificato`,
    referralWelcome: (signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `+${signals.text} ${pluralEn(signals, 'segnale bonus', 'segnali bonus')}` : null,
        promo === null ? null : `un bonus promozionale di ${promo} USDT bloccato fino al raggiungimento della condizione`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0 ? 'La tua qualifica da invito è completata' : `Qualifica completata: ${perks.join(', ')}`
    },
    referralRevoked: (name: string | null, earlyWithdrawal: boolean): string =>
      name === null
        ? earlyWithdrawal
          ? 'Qualifica revocata: un prelievo è stato richiesto durante il periodo di blocco. Segnali bonus e bonus promozionale ritirati'
          : 'Qualifica revocata: segnali bonus e bonus promozionale ritirati'
        : `La qualifica del tuo invitato ${name} è stata revocata: i segnali bonus ottenuti grazie a lui sono ritirati`,
    referralRewardPaid: (amount: string): string => `${amount} USDT pagati dal programma partner`,
    referralRewardRejected: (amount: string): string => `Una ricompensa di ${amount} USDT è stata rifiutata dall’operatore`,
    partnerLevelReached: (level: string, amount: string): string =>
      `Livello ${level} raggiunto: un bonus di ${amount} USDT è in attesa di pagamento`,
    title: 'Notifiche',
    settingsTitle: 'Impostazioni delle notifiche',
    settingsHint: 'Una categoria disattivata resta nella cronologia ma non disturba: niente badge e niente push. Le push funzionano quando sono attive nel profilo.',
    channelInApp: 'Nell’app',
    channelPush: 'Push',
    channelTelegram: 'Telegram',
    telegramHint: 'Cosa invia il bot nella tua chat. Una categoria disattivata qui non arriva su Telegram ma resta nell’app.',
    telegramLinked: (name: string): string => (name === '' ? 'Telegram collegato' : `Telegram collegato: ${name}`),
    telegramLinkedHint: 'Se non sei stato tu, tocca per scollegarlo.',
    telegramUnlinked: (name: string): string => (name === '' ? 'Telegram scollegato' : `Telegram scollegato: ${name}`),
    telegramUnlinkedHint: 'Questa chat è stata collegata a un altro account.',
    categories: { deposit: 'Depositi', withdrawal: 'Prelievi', copy: 'Copy trading', trade: 'Operazioni e posizioni', signal: 'Segnali', promo: 'Bonus promozionali', referral: 'Programma partner', kyc: 'Verifica', system: 'Sistema' } as Record<'deposit' | 'withdrawal' | 'copy' | 'trade' | 'signal' | 'promo' | 'referral' | 'kyc' | 'system', string>,
    markAllRead: 'Segna tutte come lette',
    markAllFailed: 'Impossibile segnare le notifiche come lette',
    loadFailed: 'Impossibile caricare le notifiche',
    empty:
      'Nessuna notifica per ora. Qui appariranno gli accrediti, le risposte alle richieste di prelievo, i segnali e i risultati delle operazioni.',
    unread: 'Non letta',
    today: 'Oggi',
    yesterday: 'Ieri',
    depositCredited: (amount: string, asset: string) =>
      `Accreditati ${amount} ${asset}`,
    withdrawalApproved: 'Prelievo approvato',
    withdrawalRejected: 'Prelievo rifiutato',
    withdrawalSent: 'Prelievo inviato sulla rete',
    marginCall: 'Margin call',
    positionLiquidated: 'Posizione liquidata',
    copyOpened: (master: string) => `${master} ha aperto una posizione`,
    copyClosed: (master: string) => `${master} ha chiuso una posizione`,
    copyStopLoss: 'Stop loss dell’abbonamento scattato: posizioni chiuse, fondi restituiti sul conto spot',
    copyMasterStopped: 'Trader fermato: la copia è terminata, i fondi sono tornati sul conto spot',
    copyReleased: (amount: string) => `Tornato sullo spot: ${amount} USDT`,
    copyCommission: (master: string, commission: string, profit: string) =>
      master === '' ? `Commissione al trader: ${commission} USDT su ${profit} USDT di profitto` : `Commissione a ${master}: ${commission} USDT su ${profit} USDT di profitto`,
    signalLive: (title: string, until: string) => `Segnale «${title}» aperto fino a ${until}`,
    futuresLiquidated: (symbol: string, price: string) => `Posizione ${symbol} liquidata a ${price}`,
    futuresTakeProfit: (symbol: string, price: string) => `${symbol}: take profit scattato a ${price}`,
    futuresStopLoss: (symbol: string, price: string) => `${symbol}: stop loss scattato a ${price}`,
    orderFilled: (symbol: string, quantity: string, price: string) => `Ordine limite ${symbol} eseguito: ${quantity} a ${price}`,
    orderRejected: (symbol: string) => `Ordine limite ${symbol} non eseguito: il motore ha rifiutato l’operazione`,
    orderRejectedFunds: (symbol: string) => `Ordine limite ${symbol} non eseguito: margine insufficiente sul conto futures`,
    orderRejectedLeverage: (symbol: string) => `Ordine limite ${symbol} non eseguito: la leva supera il limite per questa dimensione`,
    kycApproved: 'Verifica completata',
    kycRejected: 'Verifica rifiutata',
    kycResubmit: 'La verifica richiede nuovi documenti',
    kycExpiring: (date: string) => `Il documento scade il ${date}: rinnova la verifica`,
    kycExpired: 'Il documento è scaduto: carica un documento valido',
    signalClosed: (title: string) => `Segnale chiuso: ${title}`,
    systemAlert: (check: string) => `Incidente: ${check}`,
    systemRecovered: (check: string) => `Ripristinato: ${check}`,
    heads: {
      signalLive: 'Segnale aperto',
      signalClosed: 'Segnale chiuso',
      orderFilled: 'Ordine limite eseguito',
      orderRejected: 'Ordine limite non eseguito',
      takeProfit: 'Take profit scattato',
      stopLoss: 'Stop loss scattato',
      promoUnlocked: 'Bonus promozionale sbloccato',
      promoExpired: 'Bonus promozionale scaduto',
      referralQualified: 'Invitato qualificato',
      referralWelcome: 'Qualifica completata',
      referralRevoked: 'Qualifica revocata',
      rewardPaid: 'Ricompensa pagata',
      rewardRejected: 'Ricompensa rifiutata',
      partnerLevelReached: 'Nuovo livello partner',
      kycExpiring: 'Documento in scadenza',
      kycExpired: 'Documento scaduto',
      systemAlert: 'Incidente',
      systemRecovered: 'Ripristinato',
    },
    fallback: {
      deposit: 'Deposito accreditato',
      withdrawal: 'Aggiornamento sulla richiesta di prelievo',
      trade: 'Evento sulla posizione',
      copy: 'Evento di copy trading',
      referral: 'Evento del programma partner',
      system: 'Notifica di sistema',
    },
  },

  referrals: {
    title: 'Programma partner',
    rewardKind: {
      fee_rebate: 'Quota delle commissioni',
      signal_grant: 'Segnali aggiuntivi',
      promotional_credit: 'Bonus promozionale',
      deposit_bonus: 'Quota sul deposito',
      welcome_bonus: 'Quota di benvenuto',
      level_bonus: 'Bonus di livello',
    },
    rewardStatus: {
      pending_review: 'In verifica',
      approved: 'Approvata',
      paid: 'Pagata',
      rejected: 'Rifiutata',
      reversed: 'Annullata',
    },
    /** The promo grant's own state, shown on its reward row instead of the review status. */
    promoState: {
      locked: 'Bloccato',
      unlocked: 'Accreditato',
      expired: 'Scaduto',
      revoked: 'Revocato',
    },
    /** Level names by `level.code`; an unknown code falls back to the name the server sends. */
    levelName: {
      member: 'Membro',
      bronze: 'Bronzo',
      silver: 'Argento',
      gold: 'Oro',
      platinum: 'Platino',
      ambassador: 'Ambasciatore',
    } as Record<string, string>,
    signalUnit: 'segnali',
    membersFrom: (count: Count): string => `da ${count.text} ${pluralEn(count, 'membro', 'membri')}`,
    baseLevel: 'livello iniziale',
    depositFrom: (amount: string): string => `deposito da ${amount}`,
    noWeeklySignals: 'nessun segnale',
    perWeek: (count: Count): string => `${count.text} ${pluralEn(count, 'segnale', 'segnali')}/sett.`,
    current: 'attuale',
    copyFailed: 'Impossibile copiare',
    inviteText: 'Unisciti a 19CRT con il mio codice',
    linkCopied: 'Link copiato',
    codeCopied: 'Codice copiato',
    loadError: 'Impossibile caricare il programma partner',
    loadErrorHint: 'Controlla la connessione e riprova',
    retry: 'Riprova',
    disabled:
      'Il programma partner è temporaneamente sospeso. Le tue statistiche e ricompense sono conservate.',
    yourCode: 'Il tuo codice',
    codeHint: 'Invita direttamente: le condizioni sono visibili prima della registrazione',
    openQr: 'Apri il codice QR di invito',
    qrCode: 'Codice QR',
    share: 'Condividi',
    copy: 'Copia',
    yourLevel: 'Il tuo livello',
    participant: 'Membro',
    allLevels: 'Tutti i livelli',
    progressLabel: 'Progresso verso il livello successivo',
    qualified: (count: Count): string => pluralEn(count, 'qualificato', 'qualificati'),
    nextLevel: (remaining: Count, level: string): string => `ancora ${remaining.text} al livello ${level}`,
    maxLevel: 'livello massimo',
    invited: 'Invitati',
    confirmed: 'Confermati',
    paidUsdt: 'Pagato, USDT',
    signalsReceived: 'Segnali ricevuti',
    transparentTitle: 'Ricompensa trasparente',
    transparentHint:
      'La ricompensa è calcolata solo sulle commissioni di trading effettivamente trattenute. Il deposito dell’invitato non viene ridistribuito.',
    rewards: 'Ricompense',
    rewardHistory: 'Cronologia delle ricompense',
    underReview: 'In verifica',
    emptyRewardsTitle: 'Ancora nessuna ricompensa',
    emptyRewards: 'Le ricompense appariranno dopo la prima attività qualificata del tuo team.',
    levelsRules: 'Livelli e regole',
    levelsRulesHint: 'Team, quota delle commissioni e segnali',
    inviteTitle: 'Invito a 19CRT',
    inviteSubtitle: 'Il codice verrà associato alla registrazione',
    copyLink: 'Copia il link',
    levelsTitle: 'Livelli partner',
    levelsSubtitle: 'Contano solo i membri diretti qualificati',
    loadLevelsError: 'Impossibile caricare i livelli',
    levelTeam: 'Livello e team',
    feeSignals: 'Commissione · segnali',
    depositTiersTitle: 'Percentuali per importo',
    depositTiersHint: 'Più alto è il primo deposito, maggiore è la quota per entrambi',
    tierDeposit: 'Deposito',
    tierReferrer: 'A te',
    tierWelcome: 'Nuovo',
    tierFrom: (amount: string): string => `da ${amount}`,
    levelBonus: 'Bonus di livello',
    /** The programme's rules, worded from the operator's live settings — never a copy. */
    rules: {
      title: 'Regole del programma',
      qualification: (amount: string, asset: string): string =>
        `Qualifica: un primo deposito accreditato di almeno ${amount} ${asset} in un’unica transazione.`,
      qualificationKyc: 'È richiesta la verifica dell’identità.',
      welcome: (signals: Count, days: Count): string =>
        `Il nuovo arrivato riceve ${signals.text} ${pluralEn(signals, 'segnale bonus valido', 'segnali bonus validi')} per ${days.text} ${pluralEn(days, 'giorno', 'giorni')}.`,
      referrer: (signals: Count, days: Count): string =>
        `Il partner riceve ${signals.text} ${pluralEn(signals, 'segnale bonus valido', 'segnali bonus validi')} per ${days.text} ${pluralEn(days, 'giorno', 'giorni')} per ogni invitato qualificato.`,
      promo: (amount: string, required: string, days: Count): string =>
        `Un bonus promozionale di ${amount} USDT resta bloccato e diventa denaro su spot quando il volume operato dal nuovo arrivato raggiunge ${required} USDT entro ${days.text} ${pluralEn(days, 'giorno', 'giorni')}; altrimenti scade.`,
      hold: (days: Count): string =>
        `Qualsiasi richiesta di prelievo entro ${days.text} ${pluralEn(days, 'giorno', 'giorni')} dalla qualifica la annulla insieme ai segnali bonus e al bonus promozionale.`,
      feeShare:
        'La quota di commissione del livello si calcola solo sulle commissioni di trading effettivamente incassate dall’invitato. Le ricompense in denaro sono verificate dall’operatore e pagate separatamente: non vengono mai accreditate sul saldo.',
      secondLevel: (rate: string): string => `Il partner del partner riceve ${rate} della stessa commissione.`,
      depositTiers:
        'La quota per chi invita e per il nuovo iscritto dipende dall’importo del primo deposito — vedi la tabella in questa scheda.',
      balanceShare: (percent: string): string =>
        `Il deposito deve essere almeno il ${percent} % del saldo di chi invita al momento dell’accredito.`,
      topup: (days: Count): string =>
        `Un ulteriore deposito entro ${days.text} ${pluralEn(days, 'giorno', 'giorni')} dal primo si somma ad esso, la coppia viene rivalutata in base alla tabella e la differenza è pagata una sola volta.`,
      premiumSignal: (count: string): string =>
        `L’accesso permanente al segnale premium si apre a partire da ${count} invitati qualificati.`,
      antiabuse:
        'Un dispositivo o un indirizzo condivisi con il partner o con il suo team mandano la qualifica in verifica all’operatore.',
      paused: 'Il programma è sospeso dall’operatore: non vengono create nuove qualifiche né ricompense.',
    },
    disclaimer:
      'Le ricompense partner non sono un reddito garantito. Registrazione, depositi e volume di trading da soli non generano alcun pagamento in denaro.',
    team: 'Team',
    teamStatus: {
      pending: 'in attesa di un deposito',
      flagged: 'in verifica dall’operatore',
      qualified: 'qualificato',
      revoked: 'qualifica revocata',
    },
    joined: (date: string): string => `dal ${date}`,
    showAll: (count: Count): string => `Mostra tutti (${count.text})`,
    showLess: 'Riduci',
    promoTitle: 'Bonus promozionale',
    promoLocked: (amount: string): string => `${amount} USDT bloccati`,
    promoUnlocked: (amount: string): string => `${amount} USDT accreditati su spot`,
    promoExpired: 'Termine scaduto, il bonus è perso',
    promoRevoked: 'Bonus revocato',
    promoCondition: (traded: string, required: string): string => `Volume ${traded} / ${required} USDT`,
    promoUntil: (date: string): string => `fino al ${date}`,
    promoHint: 'Il bonus diventa denaro quando il volume delle operazioni raggiunge la condizione entro il termine.',
  },

  signals: {
    title: 'Segnali',
    tabs: { today: 'Oggi', history: 'Cronologia' },
    tabsLabel: 'Sezioni dei segnali',
    access: { public: 'Pubblico', base: 'Base', premium: 'Premium', bonus: 'Bonus', agent: 'Partner' },
    status: { scheduled: 'Programmato', available: 'Disponibile', executed: 'Eseguito', expired: 'Scaduto', cancelled: 'Annullato' },
    side: { buy: 'Acquisto', sell: 'Vendita' },
    direction: { buy: 'Long · acquisto', sell: 'Short · vendita' },
    positionSide: { buy: 'Long', sell: 'Short' },
    localTime: 'ora locale',
    localClock: 'Ora locale',
    timeZone: (offset: string) => `Fuso orario ${offset}`,
    reason: {
      unavailable: 'Questo segnale non è disponibile al momento',
      already: 'Hai già eseguito questo segnale',
      expired: 'La finestra di esecuzione è scaduta',
      notReleased: 'Questo segnale non è ancora aperto',
      cancelled: 'Questo segnale è stato annullato',
      balance: 'Fondi insufficienti sul conto spot',
      balanceFutures: 'USDT insufficienti sui conti spot e futures messi insieme',
      positionConflict: 'Hai una posizione aperta su questo mercato nella direzione opposta: chiudila per eseguire il segnale',
      price: 'Il prezzo di questo mercato al momento non è disponibile: riprova tra qualche secondo',
      access: 'Questo segnale richiede un altro livello di accesso',
      kyc: 'Completa prima la verifica: dopo l’approvazione dei documenti potrai eseguire il segnale',
      minimum: 'Questo segnale prende una quota del tuo saldo totale, inferiore al suo minimo. Ricarica il saldo per eseguirlo',
      maximum: 'L’importo supera il massimo di questo segnale',
      belowMinimum: (amount: string) => `Questo segnale prende una quota del tuo saldo totale, inferiore al suo minimo di ${amount}\u00A0USDT. Ricarica il saldo per eseguirlo`,
      aboveMaximum: (amount: string) => `L’importo supera il massimo di questo segnale: al massimo ${amount} USDT`,
      notFound: 'Nessun segnale trovato per questo codice',
      generic: 'Impossibile completare l’azione. Controlla le condizioni e riprova',
      network: 'Impossibile raggiungere il server. Controlla la connessione e riprova',
    },
    enterCode: 'Inserisci il codice',
    /** The explanation line of a signal card: an open window, and a signal already executed. */
    cardHint: { available: 'Inserisci il codice del canale: vedrai direzione, importo e condizioni prima di confermare', executed: 'Il risultato e i parametri effettivi sono nella scheda Cronologia', scheduled: (time: string) => `Il codice apparirà nel canale ${atHour(time)}` },
    executing: 'In esecuzione',
    notExecuted: 'Non eseguito',
    executedLabel: 'Eseguito',
    loadSignalsError: 'Impossibile caricare i segnali',
    noSignalsToday: 'Nessun segnale oggi',
    loadErrorHint: 'Controlla la connessione e riprova',
    retry: 'Riprova',
    signalAvailable: 'Segnale disponibile',
    nextSignal: 'Prossimo segnale',
    windowOpenUntil: (time: string) => `Finestra aperta fino ${atHour(time)}`,
    opensAt: (time: string) => `Si apre ${atHour(time)}`,
    execute: 'Esegui',
    doneToday: 'Per oggi è tutto',
    newBySchedule: 'I nuovi segnali appariranno secondo il calendario',
    codeOverline: 'Codice del segnale',
    checkTerms: 'Controlla le condizioni',
    codeHint: 'Ogni codice viene pubblicato sul nostro canale Telegram: inseriscilo appena lo vedi.',
    openChannel: 'Apri il canale',
    channelLink: 'canale Telegram',
    panelTitle: 'Codice dell’operazione',
    dealsTitle: 'Operazioni con codice',
    dealsEmpty: 'Ancora nessuna operazione con codice',
    dealsEmptyHint: 'Inserisci un codice dal canale: l’operazione e il suo esito compariranno qui',
    codePlaceholder: 'Ad esempio, 1QF9ET7RQ',
    codeRequired: 'Inserisci il codice del canale',
    codeLabel: 'Codice del segnale',
    check: 'Controlla',
    schedule: 'Calendario',
    todaySignals: 'Segnali di oggi',
    noSignals: 'Nessun segnale al momento. I nuovi codici escono secondo il calendario sul nostro canale Telegram.',
    riskDisclosure:
      'Un segnale è un’idea di trading, non un risultato garantito. Prima di confermare vedrai direzione, importo, leva, commissione e condizioni di chiusura.',
    loadHistoryError: 'Impossibile caricare la cronologia dei segnali',
    historyEmpty: 'La cronologia è vuota',
    historyEmptyHint: 'Qui appariranno i segnali eseguiti e i loro parametri effettivi.',
    toSignals: 'Ai segnali',
    allExecutions: 'Tutte le esecuzioni',
    signalAccepted: 'Segnale accettato',
    confirmation: 'Conferma del segnale',
    sentToTrading: 'L’ordine è stato inviato al sistema di trading',
    checkBeforeExecute: 'Controlla le condizioni prima dell’esecuzione',
    acceptedHint: 'Toccare di nuovo non creerà un secondo ordine.',
    ready: 'Fatto',
    account: 'Conto',
    spot: 'Spot',
    futures: 'Futures',
    perpetualMarket: 'future',
    validUntil: 'Validità',
    signalName: 'Segnale',
    until: (time: string) => `fino ${atHour(time)}`,
    timeLeft: (countdown: string) => `ancora ${countdown}`,
    priceGuide: 'Prezzo indicativo',
    entryGuide: 'Ingresso indicativo',
    marketPrice: 'Mercato',
    stopLoss: 'Stop loss',
    takeProfit: 'Take profit',
    autoClose: 'Chiusura automatica',
    inDuration: (duration: string) => `tra ${duration}`,
    duration: (days: number, hours: number, minutes: number): string =>
      [days > 0 ? `${days}\u00A0g` : null, hours > 0 ? `${hours}\u00A0h` : null, minutes > 0 || (days === 0 && hours === 0) ? `${minutes}\u00A0min` : null]
        .filter((part): part is string => part !== null)
        .join(' '),
    alreadyExecuted: 'Già eseguito',
    executionUnavailable: 'Esecuzione non disponibile',
    executionAmount: 'Importo dell’esecuzione',
    marginAmount: 'Margine',
    /** Where the amount comes from: « 1% общего баланса · 997.64 USDT ». */
    shareOfBalance: (share: string, total: string) => `${share} del saldo totale · ${total}\u00A0USDT`,
    estimatedFee: 'Commissione stimata',
    totalDebit: 'Totale addebitato',
    positionSize: 'Dimensione della posizione',
    willSell: 'Da vendere',
    fundingNote: (amount: string) =>
      `Per questa operazione ${amount} USDT passeranno dal conto spot al conto futures. Alla chiusura della posizione, questo importo, tenuto conto del risultato, tornerà sul conto spot.`,
    executionRisk:
      'Un segnale è un’idea di trading, non un risultato garantito. Il prezzo di esecuzione può differire dalla stima e l’operazione può generare perdite. Conferma solo un rischio che comprendi.',
    executionRiskFutures:
      'Un segnale è un’idea di trading, non un risultato garantito. Il prezzo di ingresso può differire dalla stima e la leva amplifica sia il guadagno sia la perdita, fino alla liquidazione della posizione. Conferma solo un rischio che comprendi.',
    retryExecution: 'Ripeti l’esecuzione',
    outcomeLabel: 'risultato, USDT',
    outcomeOpen: 'in corso',
    outcome: { take_profit: 'chiuso al target', stop_loss: 'chiuso allo stop', time: 'chiuso a scadenza', manual: 'chiuso manualmente', liquidation: 'chiuso per liquidazione' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    outcomeBadge: { take_profit: 'Al target', stop_loss: 'Allo stop', time: 'A scadenza', manual: 'Manuale', liquidation: 'Liquidato' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    openBadge: 'In corso',
    executeFor: 'Esegui fino a',
    openLong: (amount: string) => `Apri long · ${amount} USDT`,
    openShort: (amount: string) => `Apri short · ${amount} USDT`,
    submittedToast: 'Segnale inviato per l’esecuzione',
    filledOf: (requested: string, filled: string) =>
      `Richiesti ${requested} USDT, eseguiti ${filled} USDT: la quantità è stata arrotondata al passo del lotto.`,
    marginRounded: (requested: string) => `Richiesti ${requested} USDT: la quantità è stata arrotondata al passo del lotto.`,
    longOpened: (margin: string, leverage: number) => `Long aperto: margine ${margin} USDT, leva ${leverage}×.`,
    shortOpened: (margin: string, leverage: number) => `Short aperto: margine ${margin} USDT, leva ${leverage}×.`,
    fundedFromSpot: (amount: string) => `${amount} USDT trasferiti dal conto spot.`,
    closesBy: (takeProfit: boolean, stopLoss: boolean, duration: string | null): string => {
      const levels = [takeProfit ? 'al take profit' : null, stopLoss ? 'allo stop loss' : null].filter((level): level is string => level !== null)
      if (levels.length === 0) return duration === null ? '' : `Si chiuderà da sola tra ${duration}`
      const exits = duration === null ? levels : [...levels, `tra ${duration}`]
      const last = exits[exits.length - 1] ?? ''
      return `Si chiuderà ${exits.length === 1 ? last : `${exits.slice(0, -1).join(', ')} o ${last}`}`
    },
    openPosition: 'Vai alla posizione',
    returnedToSpot: (amount: string) => `Tornato sullo spot: ${amount} USDT`,
    positionAccepted: 'La posizione è aperta sul conto futures',
  },

  notFound: {
    title: 'Pagina non trovata',
    message: 'Questa pagina non esiste: il link è scaduto o l’indirizzo contiene un errore.',
    action: 'Alla home',
  },
}
